/**
 * advance-shipyard.ts — per-tick ship-production pass (M10).
 *
 * Runs inside advanceTick after advance-structures so Shipyards that just
 * came online this tick are accounted for (though the applyBuildShip
 * validator gates at queue time, not spawn time, so a mid-build Shipyard
 * destruction wouldn't retroactively block an in-flight ship).
 *
 * Per planet (ascending id for determinism):
 *   1. If the queue is empty, skip.
 *   2. Tick queue[0].buildTicksRemaining down by 1.
 *   3. On reaching 0, spawn a ShipInstance at a ring around the planet
 *      (random angle via state.rng for determinism under seed) and shift
 *      the order out of the queue.
 *
 * Resources were deducted UP FRONT when the order was queued in
 * applyBuildShip, so this pass only handles the countdown + spawn.
 *
 * Multi-build concurrency: a planet with a long queue builds FIFO, one at
 * a time. The queue array is index-0-active; later entries wait their
 * turn. Simpler than parallel tracks and keeps the UI progress bar
 * unambiguous.
 */

import { nextFloat } from '../state/rng';
import { initWeaponSlots } from './scenario';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import {
  AI_CARRIER_DEFAULTS_BY_BEHAVIOUR,
  CARRIER_SQUADRON_CAPACITY,
  DEFAULT_CARRIER_ATTACK_BEHAVIOUR,
  DEFAULT_CARRIER_DEFENSE_BEHAVIOUR,
  isCarrierTemplate,
} from './balance-carrier-squadrons';
import {
  allocEntityId,
  type Empire,
  type GameState,
  type Planet,
  type ShipInstance,
} from '../state/types/index';

/** Advance ship-production queues by exactly one tick. Mutates state. */
export function advanceShipyard(state: GameState): void {
  const planets = [...state.planets.values()].sort((a, b) => a.id - b.id);
  for (const planet of planets) {
    if (planet.shipBuildQueue.length === 0) continue;
    if (planet.ownerEmpireId === null) continue;
    const empire = state.empires.get(planet.ownerEmpireId);
    if (!empire) continue;

    const order = planet.shipBuildQueue[0]!;
    order.buildTicksRemaining -= 1;
    if (order.buildTicksRemaining <= 0) {
      spawnBuiltShip(state, empire, planet, order.chassis);
      planet.shipBuildQueue.shift();
    }
  }
}

/**
 * Spawn a completed ship at a ring around the planet. Full hull / full
 * shields / full energy / weapons ready to fire. Caller is responsible
 * for already having deducted the build cost — this helper just creates
 * the ShipInstance and attaches it to state + empire.
 *
 * Position: seeded random angle on a ring of state.balanceData.build.shipSpawnRingRadius
 * around the planet's (systemX, systemY). Multiple concurrent spawns land
 * at different angles naturally because each call advances state.rng.
 */
function spawnBuiltShip(
  state: GameState,
  empire: Empire,
  planet: Planet,
  chassis: string,
): void {
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${chassis}`);
  if (!tmpl) return; // defensive — applyBuildShip validated this at queue time

  const angle = nextFloat(state.rng) * Math.PI * 2;
  const sx = planet.systemX + Math.cos(angle) * state.balanceData.build.shipSpawnRingRadius;
  const sy = planet.systemY + Math.sin(angle) * state.balanceData.build.shipSpawnRingRadius;

  const ship: ShipInstance = {
    id: allocEntityId(state),
    chassis,
    ownerEmpireId: empire.id,
    currentHull: effectiveMaxHull(state, empire, tmpl),
    currentShields: effectiveMaxShields(state, empire, tmpl),
    currentEnergy: tmpl.energyCapacity ?? 0,
    disabledTicks: 0,
    cloaked: false,
    systemId: planet.systemId,
    sx,
    sy,
    moveOrder: null,
    jumpOriginSystemId: null,
    jumpTargetSystemId: null,
    jumpArrivalAngle: null,
    jumpTargetGateId: null,
    pendingWarpGateLinkId: null,
    jumpTicksRemaining: 0,
    chargeUpTicksRemaining: 0,
    jumpFuelCostPaid: 0,
    jumpDurationTotal: 0,
    lastFiredTick: null,
    lastFiredTargetShipId: null,
    pendingJumpSystemId: null,
    pendingJumpArrivalAngle: null,
    pendingColoniseTargetId: null,
    constructionOrder: null,
    bombardOrder: null,
    weaponSlots: initWeaponSlots(
      tmpl.weaponLoadout,
      state.weapons,
      empire.researchedTechs,
    ),
    // v3M22.1 — ship-commands defaults; new ships are 'idle'.
    commandKind: 'idle',
    waypoints: [],
    guardTargetKind: null,
    guardTargetId: null,
  };

  // v3M17.1 — carrier embarked-squadron initialisation. Carriers ship
  // out of the yard fully stocked (4 docked squadrons). v3M17.5 picks
  // the Attack / Defense Behaviour defaults per-empire: AI empires
  // pick from `AI_CARRIER_DEFAULTS_BY_BEHAVIOUR` keyed on the empire's
  // AI archetype; player empires use the static `DEFAULT_CARRIER_*`
  // constants. Player can override at any time via the detail-panel
  // selectors (v3M17.4).
  if (isCarrierTemplate(tmpl)) {
    ship.embarkedSquadronCount = CARRIER_SQUADRON_CAPACITY;
    ship.rebuildProgressFighters = 0;
    if (empire.isPlayer) {
      ship.carrierAttackBehaviour = DEFAULT_CARRIER_ATTACK_BEHAVIOUR;
      ship.carrierDefenseBehaviour = DEFAULT_CARRIER_DEFENSE_BEHAVIOUR;
    } else {
      const ai = AI_CARRIER_DEFAULTS_BY_BEHAVIOUR[empire.aiArchetype];
      ship.carrierAttackBehaviour = ai.attack;
      ship.carrierDefenseBehaviour = ai.defense;
    }
  }

  state.ships.set(ship.id, ship);
  empire.shipIds.push(ship.id);
}
